import { useState, type ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { FaChevronDown, FaChevronUp } from "react-icons/fa";
import http from "../../api/apiClient";
import Spinner from "../../components/Spinner";
import { formatDutchDate } from "../../utils/dutchDate";
import { payerLabel } from "./ContributionDetails";
import { euro, monthLabel, schoolYearLabel, type Receipt, type SchoolYearOption } from "./contributionsModel";

/** Payments of a family in the school year before the one on screen. Read only: changes go through the payment. */
export default function PreviousYearPayments({ familyId, schoolYear }: {
  familyId: string;
  schoolYear: SchoolYearOption;
}): ReactElement {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [receipts, setReceipts] = useState<Receipt[] | null>(null);
  const label = schoolYearLabel(schoolYear);

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next && receipts === null) {
      http.get(`/finance/contributions/families/${familyId}/receipts`, { params: { schoolYearId: schoolYear.id } })
        .then(({ data }) => setReceipts(Array.isArray(data) ? data : []))
        .catch(() => setReceipts([]));
    }
  };

  return (
    <div className="text-sm">
      <button
        type="button"
        aria-expanded={open}
        onClick={toggle}
        className="flex w-full items-center justify-between rounded-lg px-1 py-1 text-left text-[11px] font-bold uppercase tracking-wide text-slate-500 hover:bg-white dark:hover:bg-white/5"
      >
        {t("finance.payments.previous_year.title", { year: label, defaultValue: "Previous school year ({{year}})" })}
        {open ? <FaChevronUp aria-hidden="true" /> : <FaChevronDown aria-hidden="true" />}
      </button>
      {open && (receipts === null ? (
        <Spinner />
      ) : receipts.length === 0 ? (
        <p className="mt-1 text-xs text-slate-500">
          {t("finance.payments.previous_year.empty", { defaultValue: "No payments in this school year." })}
        </p>
      ) : (
        <ul className="mt-1 divide-y divide-slate-100 rounded-lg border border-slate-200 bg-white dark:divide-white/10 dark:border-white/10 dark:bg-slate-900">
          {receipts.map((receipt) => {
            const share = receipt.linkMethod === "SPLIT"
              ? (receipt.allocations ?? []).filter((part) => part.familyId === familyId).reduce((sum, part) => sum + Number(part.amount), 0)
              : Number(receipt.amount);
            const from = receipt.source === "MANUAL" ? t("finance.payments.previous_year.cash", { defaultValue: "Cash" }) : payerLabel(t, receipt);
            const parts = (receipt.termParts ?? []).map((part) => `${monthLabel(part.termMonth)} ${euro(part.amount)}`).join(", ");
            return (
              <li key={receipt.id} data-previous-receipt={receipt.id} className="grid grid-cols-[4.5rem_4.25rem_minmax(0,1fr)] gap-2 px-2 py-1.5 text-xs">
                <span className="tabular-nums text-slate-500">{formatDutchDate(receipt.date)}</span>
                <span className="text-right font-semibold tabular-nums text-slate-900 dark:text-white">{euro(share)}</span>
                <span className="min-w-0">
                  <span className="block truncate text-slate-800 dark:text-slate-100">
                    {from}
                    {receipt.ignored && <span className="text-slate-500"> · {t("finance.payments.not_counted", { defaultValue: "not counted" })}</span>}
                  </span>
                  {parts && <span className="block truncate text-[11px] text-indigo-600 dark:text-indigo-300">{parts}</span>}
                </span>
              </li>
            );
          })}
        </ul>
      ))}
    </div>
  );
}
